import * as XLSX from "xlsx";
import html2pdf from "html2pdf.js";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import logoDelta from "@/assets/logo_delta.jpg";

export const formatarDataPtBr = (valor) => {
  if (!valor) return "-";
  const data = new Date(valor);
  if (Number.isNaN(data.getTime())) return String(valor);
  return data.toLocaleDateString("pt-BR");
};

export const formatarMoedaPtBr = (valor) => {
  return Number(valor || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL"
  });
};

export const normalizarTexto = (valor) => {
  return String(valor || "").trim();
};

export const exportarExcel = (linhas = [], nomeArquivo = "relatorio.xlsx") => {
  const sheet = XLSX.utils.json_to_sheet(linhas);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, sheet, "Relatório");
  XLSX.writeFile(workbook, nomeArquivo);
};

const formatarDataHoraGeracao = (data = new Date()) => {
  const pad2 = (valor) => String(valor).padStart(2, "0");
  const dia = pad2(data.getDate());
  const mes = pad2(data.getMonth() + 1);
  const ano = data.getFullYear();
  const hora = pad2(data.getHours());
  const minuto = pad2(data.getMinutes());
  const segundo = pad2(data.getSeconds());
  return `${dia}/${mes}/${ano} as ${hora}:${minuto}:${segundo}`;
};

const inferirTitulo = (nomeArquivo = "") => {
  const base = String(nomeArquivo || "")
    .replace(/\.pdf$/i, "")
    .replace(/^relatorio-/i, "")
    .replace(/-/g, " ")
    .trim();

  if (!base) return "Relatório";

  return `Relatório de ${base
    .split(" ")
    .map((parte) => parte.charAt(0).toUpperCase() + parte.slice(1))
    .join(" ")}`;
};

const aguardarImagem = (imagem) => new Promise((resolve) => {
  if (!imagem || imagem.complete) {
    resolve();
    return;
  }

  imagem.onload = () => resolve();
  imagem.onerror = () => resolve();
});

let logoDeltaBase64Cache = null;
const carregarLogoDeltaBase64 = async () => {
  if (logoDeltaBase64Cache) return logoDeltaBase64Cache;

  try {
    const resposta = await fetch(logoDelta);
    const blob = await resposta.blob();

    logoDeltaBase64Cache = await new Promise((resolve, reject) => {
      const leitor = new FileReader();
      leitor.onloadend = () => resolve(leitor.result);
      leitor.onerror = reject;
      leitor.readAsDataURL(blob);
    });
  } catch (error) {
    logoDeltaBase64Cache = null;
  }

  return logoDeltaBase64Cache;
};

export const exportarPdf = async (elementoHtml, nomeArquivo = "relatorio.pdf", tituloRelatorio = "") => {
  if (!elementoHtml) return;

  const titulo = tituloRelatorio || inferirTitulo(nomeArquivo);
  const dataHora = formatarDataHoraGeracao(new Date());

  const raiz = document.createElement("div");
  raiz.style.background = "#fff";
  raiz.style.color = "#000";
  raiz.style.padding = "0";
  raiz.style.fontFamily = "Arial, sans-serif";

  const cabecalho = document.createElement("div");
  cabecalho.style.display = "flex";
  cabecalho.style.alignItems = "center";
  cabecalho.style.justifyContent = "space-between";
  cabecalho.style.padding = "10px 0 14px 0";
  cabecalho.style.marginBottom = "14px";
  cabecalho.style.borderBottom = "1px solid #d8d8d8";

  const tituloEl = document.createElement("div");
  tituloEl.textContent = titulo;
  tituloEl.style.fontSize = "22px";
  tituloEl.style.fontWeight = "700";
  tituloEl.style.lineHeight = "1.2";
  tituloEl.style.marginRight = "16px";

  const logoEl = document.createElement("img");
  logoEl.src = logoDelta;
  logoEl.alt = "Logo Delta";
  logoEl.style.height = "58px";
  logoEl.style.width = "auto";
  logoEl.style.objectFit = "contain";

  cabecalho.appendChild(tituloEl);
  cabecalho.appendChild(logoEl);

  const conteudo = elementoHtml.cloneNode(true);
  conteudo.style.marginBottom = "14px";

  // Remover a paginação do conteúdo clonado
  const paginacoes = conteudo.querySelectorAll(".v-data-table__wrapper + div");
  paginacoes.forEach((el) => el.style.display = "none");
  
  // Remover também a div pai que contém a paginação (v-data-footer)
  const footers = conteudo.querySelectorAll(".v-data-footer");
  footers.forEach((el) => el.style.display = "none");

  const rodape = document.createElement("div");
  rodape.style.marginTop = "14px";
  rodape.style.paddingTop = "10px";
  rodape.style.borderTop = "1px solid #d8d8d8";
  rodape.style.fontSize = "12px";
  rodape.style.color = "#333";
  rodape.textContent = `relatório gerado em ${dataHora} - ERP DeltaDucon.`;

  raiz.appendChild(cabecalho);
  raiz.appendChild(conteudo);
  raiz.appendChild(rodape);

  const wrapper = document.createElement("div");
  wrapper.style.position = "fixed";
  wrapper.style.left = "-10000px";
  wrapper.style.top = "0";
  wrapper.style.width = "1300px";
  wrapper.style.background = "#fff";
  wrapper.appendChild(raiz);
  document.body.appendChild(wrapper);

  await aguardarImagem(logoEl);

  try {
    await html2pdf()
      .set({
        margin: [10, 10, 10, 10],
        filename: nomeArquivo,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 1.5, useCORS: true },
        jsPDF: { unit: "mm", format: "a4", orientation: "landscape" }
      })
      .from(raiz)
      .save();
  } finally {
    if (wrapper.parentNode) wrapper.parentNode.removeChild(wrapper);
  }
};

export const exportarPdfTabela = async ({
  headers = [],
  rows = [],
  nomeArquivo = "relatorio.pdf",
  tituloRelatorio = "",
  formatadoresPorCampo = {},
  cabecalhoDireitaLinhas = [],
  resumoFinalLinhas = [],
  logoNoLadoEsquerdo = false,
  tituloNoLadoDireito = false,
  subtituloRelatorioLinhas = []
} = {}) => {
  if (!Array.isArray(rows) || !rows.length) return;

  const titulo = tituloRelatorio || inferirTitulo(nomeArquivo);
  const dataHora = formatarDataHoraGeracao(new Date());
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const logoBase64 = await carregarLogoDeltaBase64();

  const colunas = (Array.isArray(headers) ? headers : [])
    .filter((header) => header && header.value)
    .map((header) => ({
      text: header.text || header.value,
      value: header.value
    }));

  const head = [colunas.map((coluna) => coluna.text)];
  const body = rows.map((row) => colunas.map((coluna) => {
    const formatador = formatadoresPorCampo[coluna.value];
    const valorBruto = row?.[coluna.value];
    const valor = typeof formatador === "function" ? formatador(valorBruto, row) : valorBruto;
    return valor === null || valor === undefined || valor === "" ? "-" : String(valor);
  }));

  const linhasCabecalhoDireita = Array.isArray(cabecalhoDireitaLinhas)
    ? cabecalhoDireitaLinhas.filter((linha) => String(linha || "").trim())
    : [];
  const linhasSubtitulo = Array.isArray(subtituloRelatorioLinhas)
    ? subtituloRelatorioLinhas.filter((linha) => String(linha || "").trim())
    : [];

  const startYTabela = Math.max(
    28,
    18 + (Math.max(linhasCabecalhoDireita.length, linhasSubtitulo.length) * 4) + 2
  );

  autoTable(doc, {
    head,
    body,
    startY: startYTabela,
    margin: { top: startYTabela, right: 10, bottom: 16, left: 10 },
    styles: { fontSize: 8, cellPadding: 2, textColor: 20, lineColor: [220, 220, 220], lineWidth: 0.2 },
    headStyles: { fillColor: [245, 245, 245], textColor: 0, fontStyle: "bold" },
    didDrawPage: () => {
      doc.setFontSize(16);
      doc.setTextColor(0);
      if (tituloNoLadoDireito) {
        doc.setFont(undefined, "bold");
        doc.text(titulo, 286, 12, { align: "right" });
        doc.setFont(undefined, "normal");
      } else {
        doc.text(titulo, logoNoLadoEsquerdo ? 46 : 10, 14);
      }

      if (linhasSubtitulo.length > 0) {
        doc.setFontSize(10);
        doc.setTextColor(20);

        let y = 18;
        linhasSubtitulo.forEach((linha) => {
          doc.text(String(linha || ""), 286, y, { align: "right" });
          y += 4;
        });
      }

      if (linhasCabecalhoDireita.length > 0) {
        doc.setFontSize(9);
        doc.setTextColor(40);

        let y = 11;
        linhasCabecalhoDireita.forEach((linha) => {
          doc.text(String(linha || ""), 286, y, { align: "right" });
          y += 4;
        });
      }

      if (logoBase64) {
        try {
          doc.addImage(logoBase64, "JPEG", logoNoLadoEsquerdo ? 10 : 250, 6, 32, 14);
        } catch (error) {
          doc.addImage(logoBase64, "PNG", logoNoLadoEsquerdo ? 10 : 250, 6, 32, 14);
        }
      }

      doc.setFontSize(9);
      doc.setTextColor(60);
      doc.text(`relatório gerado em ${dataHora} - ERP DeltaDucon.`, 10, 205);
    }
  });

  if (Array.isArray(resumoFinalLinhas) && resumoFinalLinhas.length > 0) {
    const resumoLinhas = resumoFinalLinhas.filter((linha) => String(linha || "").trim());

    if (resumoLinhas.length > 0) {
      let yInicialNecessario = (doc.lastAutoTable?.finalY || 28) + 8;
      const alturaNecessaria = resumoLinhas.length * 4 + 8;

      if (yInicialNecessario + alturaNecessaria > 200) {
        doc.addPage();
        yInicialNecessario = 20;
      }

      let y = yInicialNecessario;

      doc.setFontSize(10);
      doc.setTextColor(10);
      doc.setFont(undefined, "bold");
      doc.text("Totalizadores", 10, y);
      doc.setFont(undefined, "normal");

      y += 5;
      resumoLinhas.forEach((linha) => {
        doc.text(String(linha), 10, y);
        y += 4;
      });
    }
  }

  doc.save(nomeArquivo);
};
